import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import chevronGreyIcon from 'assets/images/icons/chevron-grey.svg';
import { useTranslation } from 'react-i18next';
import NestedSelect from 'components/form-components/NestedSelect';
import { isRTL } from 'config/config';

const TablePagination = ({
  totalRecords,
  currentPageRecords,
  currentPage,
  setCurrentPage,
  displayEntries,
  setDisplayEntries,
  listingFunction,
}) => {
  const { t } = useTranslation();
  const totalPages = useMemo(
    () => Math.ceil(totalRecords / displayEntries),
    [totalRecords, displayEntries],
  );

  const [pageInputValue, setPageInputValue] = useState(String(currentPage));

  const [inputWidth, setInputWidth] = useState(20);
  const spanRef = useRef(null);

  const paginationOptions = [
    { value: 10, displayValue: '10' },
    { value: 20, displayValue: '20' },
    { value: 50, displayValue: '50' },
    { value: 100, displayValue: '100' },
  ];

  const getDisplayValue = (list, checkValue, defaultValue = '') => {
    return list.find((o) => o.value === checkValue)?.displayValue ?? defaultValue;
  };

  const prevPage = () => {
    if (currentPage > 1) changeCurrentPage(currentPage - 1);
  };

  const nextPage = () => {
    if (currentPage < totalPages) changeCurrentPage(currentPage + 1);
  };

  const changeDisplayEntries = (value) => {
    setCurrentPage(1);
    setDisplayEntries(value);
    listingFunction(1, value);
  };

  const changeCurrentPage = (value) => {
    setCurrentPage(value);
    listingFunction(value);
  };

  useLayoutEffect(() => {
    if (spanRef.current) {
      setInputWidth(spanRef.current.offsetWidth);
    }
  }, [pageInputValue]);

  return (
    <div className='d-flex justify-content-between align-items-center py-3'>
      <div className='d-flex gap-3 align-items-center'>
        <span>Items per page</span>
        <NestedSelect
          options={paginationOptions}
          handleChange={(_, value) => changeDisplayEntries(value)}
          name='pageSize'
          toggleClasses='d-flex gap-2 px-2 border-grey-1-h-1'
          toggleComponent={
            <>
              <label className='theme-size-0_9 cursor-pointer my-1'>
                {t(getDisplayValue(paginationOptions, displayEntries))}
              </label>
              <img src={chevronGreyIcon} className='rotate-180' />
            </>
          }
        />

        <span>
          {totalRecords === 0 ? 0 : (currentPage - 1) * displayEntries + 1} -{' '}
          {(currentPage - 1) * displayEntries + currentPageRecords} of {totalRecords} results
        </span>
      </div>
      <div className='d-flex gap-2 align-items-center'>
        <img
          src={chevronGreyIcon}
          className={`cursor-pointer ${isRTL() ? 'rotate-90' : 'rotate-270'}${
            currentPage === 1 ? ' opacity-50' : ''
          }`}
          onClick={prevPage}
        />
        <div className='position-relative'>
          <span className='position-absolute invisible width-max-content' ref={spanRef}>
            {pageInputValue || '0'}
          </span>

          <input
            className='px-2 py-1 border-grey-1-h-1 rounded-sm outline-none box-content text-center'
            type='text'
            inputMode='numeric'
            autoComplete='off'
            value={pageInputValue}
            style={{
              width: `${inputWidth}px`,
            }}
            onChange={(e) => {
              if (/^[1-9]\d*$/.test(e.target.value)) {
                let val = Number(e.target.value);
                if (val < 1) val = 1;
                if (val > totalPages) val = totalPages;
                setPageInputValue(String(val));
              }
              // allow empty string
              else if (!e.target.value) setPageInputValue(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                if (pageInputValue && currentPage !== parseInt(pageInputValue))
                  changeCurrentPage(parseInt(pageInputValue));
              }
            }}
            onBlur={() => {
              setPageInputValue(String(currentPage));
            }}
            onPaste={(e) => e.preventDefault()}
          />
        </div>
        <span>/</span>
        <span>{totalPages}</span>
        <img
          src={chevronGreyIcon}
          className={`cursor-pointer ${isRTL() ? 'rotate-270' : 'rotate-90'}${
            currentPage === totalPages ? ' opacity-50' : ''
          }`}
          onClick={nextPage}
        />
      </div>
    </div>
  );
};
TablePagination.propTypes = {
  totalRecords: PropTypes.number.isRequired,
  currentPageRecords: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  setCurrentPage: PropTypes.func.isRequired,
  displayEntries: PropTypes.number.isRequired,
  setDisplayEntries: PropTypes.func.isRequired,
  listingFunction: PropTypes.func.isRequired,
};
export default TablePagination;
